import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { FormDataService } from '../../services/form-data.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CustomDateAdapter, MY_DATE_FORMATS } from '../../common/date-adapter/adapter';
import { SaveFilterNameDialogComponent } from './save-filter-name/save-filter-name-dialog.component';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';
import { SharedService } from '../../services/shared.service';

@Component({
    selector: 'app-filter-app-logs-dialog',
    imports: [
        MatDialogModule,
        MatButtonModule,
        MatCardModule,
        CommonModule,
        MatTooltipModule,
        ReactiveFormsModule,
        MatInputModule,
        MatSelectModule,
        MatIconModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatNativeDateModule,
    ],
    providers: [
        { provide: DateAdapter, useClass: CustomDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }
    ],
    templateUrl: './filter.component.html',
    styleUrl: './filter.component.scss'
})
export class FilterAppLogsDialogComponent implements OnInit {
    filterAppLogsForm: FormGroup;
    configurations: any;

    params: any | null = null;

    loading: boolean = false;
    showSaveBtn: boolean = false;

    filterMode: string = 'Save';
    filterVal: any;

    quickFilterData: any;

    constructor(private formDataService: FormDataService, private router: Router, private sharedService: SharedService, private dialog: MatDialog, public dialogRef: MatDialogRef<FilterAppLogsDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any, private fb: FormBuilder, private toastr: ToastrService) {
        this.configurations = data.configurations;
        this.quickFilterData = data.quickFilterData;
        this.filterVal = data.filterVal;
    }

    ngOnInit(): void {
        this.filterAppLogsForm = this.fb.group({
            filter_name: [''],
            module: [''],
            action: [''],
            level: [''],
            status_code: [''],
            http_method: [''],
            created_at: new FormControl<Date | null>(null),
        });

        // Load saved form data if available - read from localStorage directly
        const savedParams = localStorage.getItem('appLogsFilterParams');
        if (savedParams) {
            const patch: any = {};
            const urlParams = new URLSearchParams(savedParams);

            urlParams.forEach((value, key) => {
                if (this.filterAppLogsForm.controls[key]) {
                    if (key === 'created_at' && value) {
                        patch[key] = new Date(value);
                    } else {
                        patch[key] = value;
                    }
                }
            });

            this.filterAppLogsForm.patchValue(patch);
        }

        if (this.quickFilterData !== undefined) {
            this.filterMode = 'Update';

            const patch: any = {};

            Object.keys(this.quickFilterData).forEach((key) => {
                if (this.filterAppLogsForm.controls[key]) {
                    if ((key === 'created_at') && this.quickFilterData[key]) {
                        patch[key] = new Date(this.quickFilterData[key]);
                    } else {
                        patch[key] = this.quickFilterData[key];
                    }
                }
            });

            this.filterAppLogsForm.patchValue(patch);
        }
    }

    onClearFilters(): void {
        this.params = null;
        this.filterAppLogsForm.reset();
        this.formDataService.setFormData(this.filterAppLogsForm.value);
        localStorage.removeItem('appLogsFilterParams');
        this.dialogRef.close(this.params);
    }

    onDeleteFilter(): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent);

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.params = null;
                this.filterAppLogsForm.reset();
                this.formDataService.setFormData(this.filterAppLogsForm.value);
                localStorage.removeItem('appLogsFilterParams');
                this.loading = true;
                this.sharedService.deleteRequest('/filters/remove?id=' + this.filterVal).subscribe(() => {
                    this.loading = false;
                    this.toastr.success('Filter Deleted Successfully');
                }, (err) => {
                    this.loading = false;
                    this.toastr.error('Error: ' + err.error.message.message);
                    if (err.error.message.message === 'Please login again. Your session is expired') {
                        localStorage.clear();
                        this.router.navigate(['/']);
                    }
                });
                this.dialogRef.close();
            }
        });
    }

    onApplyFilters(): void {
        this.params = this.generateQueryParams(this.filterAppLogsForm);
        this.formDataService.setFormData(this.filterAppLogsForm.value);
        this.dialogRef.close(this.params);
    }

    onApplyAndSaveFilters(): void {
        const params = this.generateQueryParams(this.filterAppLogsForm);

        const dialogRef = this.dialog.open(SaveFilterNameDialogComponent);

        dialogRef.afterClosed().subscribe((filterName: string) => {
            if (filterName) {
                const fullParams = params ? `${params}&filter_name=${encodeURIComponent(filterName)}&filterStatus=${this.filterMode}` : `filter_name=${encodeURIComponent(filterName)}&filterStatus=${this.filterMode}`;
                this.formDataService.setFormData({
                    ...this.filterAppLogsForm.value
                });
                this.dialogRef.close(fullParams);
            }
        });
    }

    generateQueryParams(form: FormGroup): string {
        const queryParams = [];
        for (const key in form.controls) {
            if (form.controls.hasOwnProperty(key)) {
                let controlValue = form.controls[key].value;
                if (controlValue !== null && controlValue !== '') {
                    if (key === 'created_at') {
                        controlValue = this.formatDate(controlValue);
                    }
                    queryParams.push(`${key}=${encodeURIComponent(controlValue)}`);
                }
            }
        }
        return queryParams.join('&');
    }

    formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    resetDateRange(picker: any) {
        this.filterAppLogsForm.patchValue({
            created_at_start: null,
            created_at_end: null
        });
        picker.close();
    }
}